(() => {
  if (window.neutralsNavigationReady) return;
  window.neutralsNavigationReady = true;
  const controllers = new Map();
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';

  function initialize() {
    document.querySelectorAll('.header__mobile').forEach(menu => {
      if (controllers.has(menu)) return;
      const trigger = menu.querySelector('summary');
      const panel = menu.querySelector('.header__drawer');
      const backdrop = menu.querySelector('.header__backdrop');
      const links = [...panel.querySelectorAll('a')];
      const header = menu.closest('.header');
      let target = false;
      let revision = 0;
      let animations = [];
      let restoreScroll = null;

      function measure() {
        header.style.setProperty('--navigation-header-height', `${header.getBoundingClientRect().bottom}px`);
      }
      const observer = new ResizeObserver(measure);
      observer.observe(header);
      measure();

      function lock() {
        if (restoreScroll) return;
        const root = document.documentElement;
        const body = document.body;
        const saved = [root.style.overflow, body.style.overflow, body.style.paddingRight];
        const gap = innerWidth - root.clientWidth;
        if (gap) body.style.paddingRight = `${parseFloat(getComputedStyle(body).paddingRight) + gap}px`;
        root.style.overflow = 'hidden';
        restoreScroll = () => {
          root.style.overflow = saved[0];
          body.style.overflow = saved[1];
          body.style.paddingRight = saved[2];
          restoreScroll = null;
        };
      }
      function settle(open) {
        trigger.setAttribute('aria-expanded', String(open));
        trigger.setAttribute('aria-label', open ? 'Close navigation menu' : 'Open navigation menu');
        menu.dataset.state = open ? 'open' : 'closed';
        menu.open = open;
        panel.inert = !open;
        animations.forEach(animation => animation.cancel());
        animations = [];
        if (!open) restoreScroll?.();
      }
      function change(open, focus = false) {
        target = open;
        const current = ++revision;
        const elements = [panel, backdrop, ...links];
        // Sample before cancellation, so interrupted transitions start where they are.
        const from = elements.map(element => {
          const style = getComputedStyle(element);
          return { opacity: style.opacity, transform: style.transform };
        });
        animations.forEach(animation => animation.cancel());
        animations = [];
        menu.open = true; // Keep the native disclosure visible through its exit animation.
        menu.dataset.state = open ? 'opening' : 'closing';
        trigger.setAttribute('aria-expanded', String(open));
        trigger.setAttribute('aria-label', open ? 'Close navigation menu' : 'Open navigation menu');
        panel.inert = !open;
        if (focus) trigger.focus({ preventScroll: true });
        if (open) { lock(); measure(); }
        if (motion.matches || !panel.animate) { settle(open); return; }
        const duration = 200;
        animations = elements.map((element, index) => {
          const isLink = index > 1;
          const order = open ? index - 2 : links.length - 1 - (index - 2);
          const delay = isLink ? Math.min(order * 15, 60) : 0;
          const end = { opacity: open ? 1 : 0, transform: index === 1 ? 'none' : `translateY(${open ? 0 : -8}px)` };
          return element.animate([from[index], end], {
            duration: isLink ? duration - 60 : duration,
            delay, easing, fill: 'both'
          });
        });
        Promise.all(animations.map(animation => animation.finished)).then(() => {
          if (current === revision) settle(open);
        }).catch(() => { /* A newer transition owns completion after cancellation. */ });
      }
      menu.dataset.enhanced = 'true';
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', 'Open navigation menu');
      settle(false);
      const activate = event => {
        event.preventDefault();
        change(!target, target);
      };
      trigger.addEventListener('click', activate);
      const controller = {
        menu,
        close(focus = false) { if (target) change(false, focus); },
        follow(link) {
          const destination = new URL(link.href, location.href);
          const samePageAnchor = destination.origin === location.origin && destination.pathname === location.pathname && destination.search === location.search && destination.hash;
          if (samePageAnchor) {
            // Let native hash scrolling start immediately, alongside the visual exit.
            restoreScroll?.();
            if (target) change(false);
          } else {
            // Navigation must not inherit an exiting overlay or a cached scroll lock.
            ++revision; target = false; settle(false);
          }
        },
        finish() { ++revision; settle(target); },
        destroy() { ++revision; target = false; settle(false); observer.disconnect(); trigger.removeEventListener('click', activate); controllers.delete(menu); }
      };
      controllers.set(menu, controller);
    });
  }
  document.addEventListener('click', event => {
    controllers.forEach(controller => {
      const { menu } = controller;
      const link = event.target.closest('.header__drawer a');
      if (link && menu.contains(link)) { controller.follow(link); return; }
      if (event.target.closest('.header__backdrop') || !menu.contains(event.target) || event.target.closest('a')) controller.close();
    });
  });
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') controllers.forEach(controller => controller.close(true));
  });
  motion.addEventListener('change', () => controllers.forEach(controller => controller.finish()));
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', event => {
    controllers.forEach(controller => { if (event.target.contains(controller.menu)) controller.destroy(); });
  });
  // Clear overlays and scroll locks before an outgoing native snapshot, including history navigation.
  window.addEventListener('pageswap', () => controllers.forEach(controller => controller.destroy()));
  window.addEventListener('pagehide', () => controllers.forEach(controller => controller.destroy()));
  window.addEventListener('pageshow', initialize);
  initialize();
})();
